// src/components/AfterSales.js
import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { sendAfterSaleEmail, fetchOrderHistory } from '../api/orderApi';
import AlertModal from './AlertModal';
import styles from '../styles/AfterSales.module.css';

const AfterSales = () => {
  // 使用 i18n 的翻译钩子
  const { t } = useTranslation();

  const [orderId, setOrderId] = useState('');
  const [requestType, setRequestType] = useState('support');
  const [issueType, setIssueType] = useState('');
  const [description, setDescription] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isManualOrder, setIsManualOrder] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [isAlertOpen, setIsAlertOpen] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');
  const [orderHistory, setOrderHistory] = useState([]);

  // 从 Redux 获取用户信息
  const { user } = useSelector((state) => state.user);

  // 问题类型选项
  const issueTypeOptions = [
    { value: '', label: t('afterSales.issueType.placeholder') },
    { value: 'account-access', label: t('afterSales.issueType.accountAccess') },
    { value: 'account-expired', label: t('afterSales.issueType.accountExpired') },
    { value: 'technical-issue', label: t('afterSales.issueType.technicalIssue') },
    { value: 'not-as-described', label: t('afterSales.issueType.notAsDescribed') },
    { value: 'other', label: t('afterSales.issueType.other') },
  ];

  // 获取订单历史数据
  useEffect(() => {
    console.log('useEffect triggered, user:', user);

    const loadOrderHistory = async () => {
      if (user && user?.user_key) {
        console.log('User exists, user_key:', user.user_key);
        try {
          console.log('Fetching order history...');
          const response = await fetchOrderHistory({ user_key: user.user_key });
          console.log('Response from fetchOrderHistory:', response);
          const orders = response.data.get_order_history || [];
          setOrderHistory(orders);
          console.log('Order history set:', orders);
        } catch (error) {
          console.error('Error fetching order history:', error);
          setErrorMessage(t('afterSales.errors.loadOrderHistory'));
        }
      } else {
        console.log('No user or user_key, skipping fetch');
        setErrorMessage(t('afterSales.errors.loginRequired'));
      }
    };

    loadOrderHistory();
  }, [user, t]);

  const handleOrderSelect = (value) => {
    if (value === 'manual') {
      setIsManualOrder(true);
      setOrderId('');
    } else {
      setIsManualOrder(false);
      setOrderId(value);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setErrorMessage('');
    setIsAlertOpen(false);

    try {
      if (!user || !user.uid) {
        throw new Error(t('afterSales.errors.loginToSubmit'));
      }

      console.log('Submitting after-sale request with:', { orderId, requestType, issueType, description, contactEmail, uid: user.uid });
      const response = await sendAfterSaleEmail({
        orderNo: orderId,
        type: requestType === 'refund' ? t('afterSales.requestTypeLabels.refund') : t('afterSales.requestTypeLabels.support'),
        question: issueType,
        desc: description,
        email: contactEmail,
        uid: user.uid,
      });

      console.log('sendAfterSaleEmail response:', response);
      if (response.data && response.data.result === 1) {
        setAlertMessage(t('afterSales.messages.submitSuccess'));
        setIsAlertOpen(true);
        setOrderId('');
        setRequestType('support');
        setIssueType('');
        setDescription('');
        setContactEmail('');
        setIsManualOrder(false);
      } else {
        setAlertMessage(t('afterSales.messages.submitFailed'));
        setIsAlertOpen(true);
      }
    } catch (error) {
      setAlertMessage(t('afterSales.messages.submitError', { error: error.message || t('afterSales.errors.unknownError') }));
      setIsAlertOpen(true);
      console.error('Error submitting after-sale request:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAlertClose = () => {
    setIsAlertOpen(false);
  };

  return (
    <section className={styles.afterSalesSection}>
      <div className={styles.container}>
        <div className={styles.afterSalesContainer}>
          <h2 className={styles.sectionTitle}>{t('afterSales.title')}</h2>

          {errorMessage && (
            <div className={`${styles.notification} ${styles.error}`}>
              <i className="fas fa-exclamation-circle"></i>
              <span>{errorMessage}</span>
            </div>
          )}

          <form id="after-sales-form" onSubmit={handleSubmit}>
            <div className={styles.formGroup}>
              <label htmlFor="order-id">
                {t('afterSales.orderNumber.label')} <span style={{ color: 'var(--error-color)' }}>*</span>
              </label>
              {!isManualOrder ? (
                <select
                  id="order-select"
                  name="order-select"
                  value={orderId}
                  onChange={(e) => handleOrderSelect(e.target.value)}
                  required
                >
                  <option value="">{t('afterSales.orderNumber.placeholder')}</option>
                  {orderHistory.map((order) => (
                    <option key={order.orderNo} value={order.orderNo}>
                      {order.orderNo} - {order.product_name}
                    </option>
                  ))}
                  <option value="manual">{t('afterSales.orderNumber.manualInput')}</option>
                </select>
              ) : (
                <input
                  type="text"
                  id="order-id"
                  name="order-id"
                  placeholder={t('afterSales.orderNumber.inputPlaceholder')}
                  value={orderId}
                  onChange={(e) => setOrderId(e.target.value)}
                  required
                  style={{ marginTop: '10px' }}
                />
              )}
              <span className={styles.formHint}>{t('afterSales.orderNumber.hint')}</span>
            </div>

            <div className={styles.formGroup}>
              <label>
                {t('afterSales.requestType.label')} <span style={{ color: 'var(--error-color)' }}>*</span>
              </label>

              <div className={styles.formRadioGroup}>
                <label
                  className={`${styles.radioCard} ${requestType === 'support' ? styles.active : ''}`}
                  onClick={() => setRequestType('support')}
                >
                  <input
                    type="radio"
                    id="support"
                    name="request-type"
                    value="support"
                    checked={requestType === 'support'}
                    onChange={() => setRequestType('support')}
                  />
                  <div className={styles.radioCardContent}>
                    <div className={styles.radioCardTitle}>{t('afterSales.requestType.support.title')}</div>
                    <div className={styles.radioCardDesc}>{t('afterSales.requestType.support.description')}</div>
                  </div>
                </label>

                {/* 退款申请选项被注释掉，但保留翻译键以备将来使用 */}
                {/* <label
                  className={`${styles.radioCard} ${requestType === 'refund' ? styles.active : ''}`}
                  onClick={() => setRequestType('refund')}
                >
                  <input
                    type="radio"
                    id="refund"
                    name="request-type"
                    value="refund"
                    checked={requestType === 'refund'}
                    onChange={() => setRequestType('refund')}
                  />
                  <div className={styles.radioCardContent}>
                    <div className={styles.radioCardTitle}>{t('afterSales.requestType.refund.title')}</div>
                    <div className={styles.radioCardDesc}>{t('afterSales.requestType.refund.description')}</div>
                  </div>
                </label> */}
              </div>
            </div>

            <div className={styles.formGroup}>
              <label htmlFor="issue-type">
                {t('afterSales.issueType.label')} <span style={{ color: 'var(--error-color)' }}>*</span>
              </label>
              <select
                id="issue-type"
                name="issue-type"
                value={issueType}
                onChange={(e) => setIssueType(e.target.value)}
                required
              >
                {issueTypeOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            <div className={styles.formGroup}>
              <label htmlFor="description">
                {t('afterSales.description.label')} <span style={{ color: 'var(--error-color)' }}>*</span>
              </label>
              <textarea
                id="description"
                name="description"
                placeholder={t('afterSales.description.placeholder')}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                required
              />
            </div>

            <div className={styles.formGroup}>
              <label htmlFor="contact-email">
                {t('afterSales.contactEmail.label')} <span style={{ color: 'var(--error-color)' }}>*</span>
              </label>
              <input
                type="email"
                id="contact-email"
                name="contact-email"
                placeholder={t('afterSales.contactEmail.placeholder')}
                value={contactEmail}
                onChange={(e) => setContactEmail(e.target.value)}
                required
              />
              <span className={styles.formHint}>{t('afterSales.contactEmail.hint')}</span>
            </div>

            <div className={styles.formButtons}>
              <button type="button" className={`${styles.btn} ${styles.btnSecondary}`} onClick={() => window.history.back()}>
                {t('afterSales.buttons.back')}
              </button>
              <button type="submit" className={styles.btn} disabled={isSubmitting}>
                {isSubmitting && <span className={styles.loader}></span>}
                <span>{isSubmitting ? t('afterSales.buttons.submitting') : t('afterSales.buttons.submit')}</span>
              </button>
            </div>
          </form>

          <AlertModal
            isOpen={isAlertOpen}
            message={alertMessage}
            onClose={handleAlertClose}
          />
        </div>
      </div>
    </section>
  );
};

export default AfterSales;